// src/utils/bootstrap.js
import axios from 'axios';
import rocketChatService from '../services/rocketChatService';
import patientListService from '../services/patientListService';
import router from '../router';
import { isEmbedMode } from './embedMode';
import sessionManager from './sessionManager';
import { captureAndStripUrlToken } from './urlToken';
import { requestEmbedToken } from './embedAuth';
import { installGlobalAuthRefresh } from './authInterceptors';
import { CLINICAL_CALENDAR_ONLY } from './viScope';

// Optional: read exp from a JWT if your token is a JWT
function readJwtExp(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp : undefined;
  } catch { 
    return undefined; 
  }
}

// Fetch user info from utility/getUserInfoV2 endpoint
async function fetchUserInfo(store) {
  try {

    const response = await patientListService.get('/utility/getUserInfoV2');
    const userInfo = response.data.result? response.data.result : null;

    // Store user info in Vuex store and sessionStorage
    store.dispatch('login/setUserInfo', userInfo);

    // The RBAC role -> permission catalog is fetched here rather than from the
    // myPractice userInfo store's own getUserInfo action: Navbar and the settings
    // pages skip that action entirely once login.userInfo is cached above, so this
    // is the one place the catalog is guaranteed to load. It has to follow the
    // user info rather than run alongside it: getRbacRoles is a no-op unless the
    // clinic is on the general_beta1 program, which is read off it. Awaited so
    // the permissions are in the store before the app renders and guards run;
    // never rejects.
    await store.dispatch('myPractice/userInfo/getRbacRoles')
      .catch(error => { console.error('Failed to fetch RBAC roles:', error); });

    return userInfo;

  } catch (error) {
    console.error('Failed to fetch user info:', error);
    return null;
  }
}

export async function bootstrapAuth(store) {
  // The token is captured and stripped from the URL in main.js BEFORE the
  // router is installed (see captureAndStripUrlToken). Reading it here is
  // idempotent: it returns the already-cached value without touching the URL.
  // If main.js didn't run it for some reason, this captures+strips now.
  let { token, userId } = captureAndStripUrlToken();

  // VP-18504: when embedded in va-portal the token no longer rides in the iframe
  // URL — ask the parent for it over postMessage. A token received this way is
  // handled exactly like a URL token (fresh session from va-portal).
  if (!token && isEmbedMode()) {
    token = await requestEmbedToken();
  }

  // Capture whether this load carried a fresh JWT (URL or embed handshake). A
  // fresh token means a brand-new session; otherwise we are resuming an
  // existing one on reload.
  const urlToken = token;

  if (urlToken) {
    // NEW SESSION: store the URL JWT immediately, then exchange it for a
    // long-lived refresh token and a fresh access token (see sessionManager).
    // Clear any leftover refresh token first: it belongs to a previous session
    // and, if the exchange below fails, must not be reused to refresh into the
    // wrong user's access token (sessionManager.initialize() would otherwise
    // pick it up). The exchange repopulates it on success.
    const urlExp = readJwtExp(urlToken);
    store.dispatch('login/setToken', {
      token: urlToken,
      userId,
      exp: urlExp,
      refreshToken: '',
      refreshTokenExpiry: 0,
      // New session: explicitly clear any prior session's RC creds (SET_TOKEN now
      // preserves these when omitted). They are repopulated by /auth/rc-session.
      authToken: '',
      rc_user_id: '',
      ws_url: '',
    });

    const ok = await sessionManager.bootstrapNewSession(urlToken);
    if (!ok) {
      // Backend may not have the refresh endpoints yet, or the call failed —
      // fall back to using the URL token directly so the app still works.
      console.warn('Refresh-token bootstrap failed; falling back to the URL token.');
    }
    // Use whichever access token is now current (refreshed or the URL token).
    token = store.getters['login/token'] || urlToken;
  } else {
    // RELOAD: resume from the stored session token, refreshing it if expired.
    const existsToken = sessionStorage.getItem('token');
    if (existsToken && sessionManager.getExistingSessionExpiry()) {
      // The refresh-token-backed session has already ended (refresh-token ceiling
      // or demo absolute limit) — even if the access-token JWT is still valid by
      // its own exp. Drop the token NOW: otherwise the authenticated bootstrap
      // tasks below (rc-session, fetchUserInfo) would run on a dead session before
      // sessionManager.initialize() detects expiry, allowing brief post-expiry
      // API access. This mirrors initialize()'s getExistingSessionExpiry() check.
      sessionStorage.removeItem('token');
      store.dispatch('login/clear');
    } else if (existsToken) {
      // The expiry comes from the refresh/exchange API response (persisted into
      // login/exp + sessionStorage). That is authoritative — use it. Only decode
      // the JWT as a fallback when nothing is stored (e.g. URL-token-only mode).
      // Decoding alone was a bug: opaque/non-JWT tokens make readJwtExp return
      // undefined, which wrongly marked a still-valid token as expired and forced
      // a needless refresh (or premature logout when no refresh token remained).
      const existingTokenExp =
        (store.getters['login/exp'] ||
          parseInt(sessionStorage.getItem('exp') || '0', 10)) ||
        readJwtExp(existsToken);
      const now = Math.floor(Date.now() / 1000);
      const accessValid = existingTokenExp && existingTokenExp > now;

      if (accessValid) {
        token = existsToken;
      } else if (!sessionManager.isRefreshTokenExpired()) {
        // Access token expired but the refresh token is still valid — get a new
        // access token rather than forcing a re-login. Use the shared single-flight.
        const result = await sessionManager.sharedRefresh();
        if (result && result.success) {
          token = store.getters['login/token'];
        } else if (result && result.error === 'unauthorized') {
          // Refresh token genuinely rejected — the session is over.
          sessionStorage.removeItem('token');
          store.dispatch('login/clear');
        } else {
          // Transient failure (5xx / network). Do NOT clear — the refresh token is
          // still valid. Keep the existing (expired) access token and proceed;
          // on-demand refresh + the periodic session check recover the session.
          token = existsToken;
        }
      } else {
        sessionStorage.removeItem('token');
        store.dispatch('login/clear');
      }
    }
  }
  
  // On reload the URL no longer carries userId (it was stripped on first load),
  // so fall back to the persisted value. Otherwise SET_TOKEN would coerce the
  // missing userId to '' and wipe X-User-Id (used by requestConfig / pricingService).
  if (!userId) {
    userId = store.getters['login/userId'] || '';
  }

  // Capture embed-mode flag from the (still-intact) URL before any
  // history.replaceState below could strip query params later.
  const embedded = isEmbedMode();

  // Process the token (from URL or sessionStorage)
  if (token) {
    // Prefer the expiry the refresh/exchange API already gave us. sessionManager
    // stores the response's `token_expiry` into state.exp via updateTokens, so
    // read that back here. Only decode the JWT as a fallback when nothing is
    // stored (e.g. URL-token-only mode where the refresh endpoints were
    // unavailable). Re-decoding unconditionally was a bug: readJwtExp returns
    // undefined when the token isn't a decodable JWT, and SET_TOKEN's `exp || 0`
    // would then clobber the authoritative expiry from the API response with 0.
    const exp = store.getters['login/exp'] || readJwtExp(token);

    // Store basic token info immediately so RocketChatService interceptor can access it
    store.dispatch('login/setToken', {
      token: token,
      userId,
      exp
    });

    if (embedded || CLINICAL_CALENDAR_ONLY) {
      // In embed mode the iframe only renders practice-setting and does not
      // use chat/WebSocket, so skip the rc-session call entirely. Same when
      // only the Clinical Calendar is enabled (VP-18687): RocketChat backs the
      // hidden My Patients chat only, and this call was surfacing as
      // "Network Error" pop-ups on every login.
      await fetchUserInfo(store);
    } else {
      try {
        // Call auth/rc-session endpoint to get authToken and rc_user_id
        const response = await rocketChatService.get('/auth/rc-session', {
          token // Send the original token to get RocketChat credentials
        });

        const { auth_token, rc_user_id, ws_url, support_room, internal_room } = response.data;

        // Update store with RocketChat credentials (token already stored above)
        store.dispatch('login/setToken', {
          token: token,
          userId,
          exp,
          authToken: auth_token,
          rc_user_id: rc_user_id,
          ws_url: ws_url,
          support_room: support_room,
          internal_room: internal_room
        });

        // Fetch user info after successful token storage
        await fetchUserInfo(store);

      } catch (error) {
        console.error('Failed to get RocketChat session:', error);

        // The rc-session call runs ensureFreshToken first (rocketChatService's
        // attachTokenRefresh interceptor). A refresh 401 triggers
        // handleSessionExpiry, which clears auth (login/clear → token '') and
        // schedules a logout. In that case do NOT write the stale token back —
        // doing so would resurrect the dead session that was just torn down.
        // Only restore the (RC-less) token state when auth is still intact.
        if (store.getters['login/token']) {
          store.dispatch('login/setToken', { token, userId, exp });

          // Still fetch user info even if RC session fails
          await fetchUserInfo(store);
        }
      }
    }
  } else {
    // Clear any stale data
    store.dispatch('login/clear');
  }

  // Check if we have token but missing user info (refresh scenario or first time)
  const currentToken = store.getters['login/token'];
  const currentUserInfo = store.getters['login/userInfo'];
  
  if (currentToken && !currentUserInfo) {
    await fetchUserInfo(store);
  }

  let path = router.currentRoute.value.path;

  // Set Axios default header once if we have a token
  const finalToken = store.getters['login/token'];
  const finalUserId = store.getters['login/userId'];
  
  if (finalToken) {
    // Set default headers for axios (initial value; the global interceptor below
    // re-applies the CURRENT token on every direct axios call so it never goes
    // stale after a refresh).
    axios.defaults.headers.common['X-Auth-Token'] = finalToken;
    if (finalUserId) {
      axios.defaults.headers.common['X-User-Id'] = finalUserId;
    }

    // Direct axios.get/post calls bypass the per-instance interceptors; install
    // on-demand refresh + fresh auth headers on the default instance too.
    installGlobalAuthRefresh();

    // Start session management (silent refresh + absolute timeout) now that a
    // valid token is in place.
    const sessionOk = await sessionManager.initialize();

    // initialize() returns false when there is no usable session. The 401 path
    // already cleared the store + redirected via handleSessionExpiry; but the
    // terminal fallback case (no refresh token + expired access token) only ran
    // cleanup(), leaving the stale token in the store and on axios headers while
    // the global auth-refresh interceptor stays active. Detect that by the token
    // still being present, and tear the session down properly so the app does
    // not mount in a broken, authed-looking state.
    if (!sessionOk && store.getters['login/token']) {
      sessionManager.handleSessionExpiry('token_refresh_failed');
    }

    // Load the provider's authoritative timezone setting once, app-wide, so every
    // calendar/notification consumer uses the REST value from the start. This is the
    // single source of truth — getOrCreateCalendar no longer sets the timezone, which
    // removes the race where calendar creation could revert it to a different zone.
    store.dispatch('calendar/getUserTimezone').catch((error) => {
      console.error('Failed to load timezone setting:', error);
    });
  }
}